/*
 * Multi-step kit configurator.
 *
 * Architecture:
 * - Liquid renders the option cards and a JSON blob (#configurator-config) with
 *   every price, image and id we need. The DOM is never read back for data.
 * - All choices live in one plain `state` object. `render()` projects that
 *   state onto the DOM; event handlers only mutate state and call `render()`.
 * - Each step type is described once in STEPS. A step says how a change
 *   updates state, whether it is complete, which cart lines it contributes and
 *   which preview image it shows. The subtotal and the cart payload are both
 *   derived from the same `buildLines()`, so they cannot disagree.
 *
 * To add a step: render a `[data-step data-step-type="x"]` block in the
 * section, add its data to the JSON config, and add an `x` entry to STEPS.
 */

const NONE = '0';

function initConfigurator(root) {
  const $ = (selector) => root.querySelector(selector);
  const $$ = (selector) => Array.from(root.querySelectorAll(selector));

  const config = JSON.parse($('#configurator-config').textContent);
  const products = new Map(config.products.map((product) => [product.id, product]));

  const lengthInput = $('#meters-input');
  const nextBtn = $('[data-next]');
  const prevBtn = $('[data-prev]');
  const addToCartBtn = $('[data-add-to-cart]');
  const progressEl = $('.configurator__progress');
  const statusEl = $('[data-progress-status]');
  const lengthEl = $('#configurator__length');
  const totalEl = $('#configurator__total');
  const subtotalEl = $('[data-subtotal]');
  const subtotalLabelEl = $('[data-subtotal-label]');
  const errorEl = $('[data-configurator-error]');
  const previewEl = $('#configurator__preview');
  const initialPreviewSrc = previewEl ? previewEl.getAttribute('src') || '' : '';
  const defaultLength = parseFloat(lengthInput.defaultValue) || 1;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  let isAdding = false;
  let state = createState();

  function createState() {
    const firstProduct = config.products[0];
    return {
      started: false, // false while the intro screen is showing
      stepId: 'product',
      visited: new Set(['product']),
      length: defaultLength, // metres, or null while the input is empty
      product: firstProduct ? firstProduct.id : null,
      variant: null,
      channel: NONE,
      endCap: config.endCaps.length ? config.endCaps[0].id : null,
      dimmer: config.dimmers.length ? '0' : null,
    };
  }

  function activeProduct() {
    return products.get(state.product) || { variants: [], channels: [] };
  }

  // Length in whole decimetres (0.1m units). Rounding avoids float errors such
  // as 1.1 * 10 = 11.000000000000002 being ceiled up to 12.
  function decimetres() {
    return state.length ? Math.round(state.length * 10) : 0;
  }

  // The smallest driver kit that can power the current length.
  function pickDriver(dimmer) {
    if (!dimmer) return null;
    const length = state.length || 0;
    return (
      dimmer.drivers
        .filter((driver) => length <= driver.maxLength)
        .sort((a, b) => a.maxLength - b.maxLength)[0] || null
    );
  }

  function driverTotal(driver) {
    return driver.lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
  }

  // Keep dependent choices consistent after any change.
  function normalize() {
    const product = activeProduct();
    if (!product.variants.some((variant) => variant.id === state.variant)) {
      state.variant = product.variants.length ? product.variants[0].id : null;
    }
    if (state.channel !== NONE && !product.channels.some((channel) => channel.id === state.channel)) {
      state.channel = NONE;
    }
    if (state.dimmer !== null && !pickDriver(config.dimmers[Number(state.dimmer)])) {
      state.dimmer = null; // no driver can power this length
    }
  }

  // ---------------------------------------------------------------------------
  // Step definitions
  // ---------------------------------------------------------------------------

  const STEPS = {
    length: {
      isValid: () => state.length !== null,
      onChange(input) {
        const min = parseFloat(input.min) || 1;
        const max = parseFloat(input.max) || min;
        const value = parseFloat(input.value);
        if (isNaN(value)) {
          state.length = null;
          return;
        }
        state.length = Math.round(Math.min(max, Math.max(min, value)) * 10) / 10;
        input.value = state.length;
      },
    },

    product: {
      field: 'product',
      isValid: () => !!state.product,
      image: () => activeProduct().image,
    },

    variant: {
      field: 'variant',
      isValid: () => !!state.variant,
      lines() {
        const product = activeProduct();
        const variant = product.variants.find((v) => v.id === state.variant);
        if (!variant) return [];
        return [
          {
            id: variant.id,
            price: variant.price,
            quantity: product.metered ? decimetres() : 1,
            main: true,
          },
        ];
      },
    },

    channel: {
      field: 'channel',
      isApplicable: () => activeProduct().channels.length > 0,
      isValid: () => true,
      lines() {
        const channel = activeProduct().channels.find((c) => c.id === state.channel);
        if (!channel) return [];
        return [{ id: channel.id, price: channel.price, quantity: Math.ceil(state.length || 0) }];
      },
      image() {
        const channel = activeProduct().channels.find((c) => c.id === state.channel);
        return channel ? channel.image : activeProduct().image;
      },
    },

    'end-cap': {
      field: 'endCap',
      isValid: () => state.endCap !== null,
      lines() {
        const endCap = selectedEndCap();
        return endCap && Number(endCap.id) > 0 ? [{ id: endCap.id, price: endCap.price, quantity: 1 }] : [];
      },
      // Recorded on the main tape line so the team can see it in the order.
      mainProperties() {
        const endCap = selectedEndCap();
        return endCap ? { 'End cap type': endCap.title } : {};
      },
      image() {
        const endCap = selectedEndCap();
        return endCap && endCap.image;
      },
    },

    dimmer: {
      field: 'dimmer',
      isValid: () => state.dimmer !== null,
      lines() {
        const driver = pickDriver(config.dimmers[Number(state.dimmer)]);
        return driver ? driver.lines.map((line) => ({ ...line })) : [];
      },
      // The first option is the baseline (no dimming), whose image is a "none"
      // icon - not worth previewing - so it falls back to the tape photo.
      image() {
        const dimmer = config.dimmers[Number(state.dimmer)];
        const hasPreview = state.dimmer !== null && Number(state.dimmer) > 0 && dimmer && dimmer.image;
        return hasPreview ? dimmer.image : activeProduct().image;
      },
    },
  };

  function selectedEndCap() {
    return config.endCaps.find((endCap) => endCap.id === state.endCap);
  }

  // Steps present on this page, in DOM order. Metafields decide which exist.
  const allSteps = $$('[data-step]')
    .map((el) => ({ el, type: el.dataset.stepType, def: STEPS[el.dataset.stepType] }))
    .filter((step) => step.def);

  function visibleSteps() {
    return allSteps.filter((step) => !step.def.isApplicable || step.def.isApplicable());
  }

  // ---------------------------------------------------------------------------
  // Cart lines - the single source for both the subtotal and the cart payload
  // ---------------------------------------------------------------------------

  function buildLines() {
    const lines = [];
    const properties = {};
    visibleSteps().forEach(({ def }) => {
      if (def.lines) lines.push(...def.lines());
      if (def.mainProperties) Object.assign(properties, def.mainProperties());
    });

    const main = lines.find((line) => line.main);
    if (main && Object.keys(properties).length) main.properties = properties;

    config.autoIncluded.forEach((item) => lines.push({ id: item.id, price: item.price, quantity: 1 }));

    // "None" choices have an id of 0 and empty quantities are dropped
    return lines.filter((line) => Number(line.id) > 0 && line.quantity > 0);
  }

  function subtotal() {
    return buildLines().reduce((sum, line) => sum + line.price * line.quantity, 0);
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  function render() {
    normalize();
    const visible = visibleSteps();
    if (!visible.some((step) => step.type === state.stepId)) state.stepId = visible[0].type;
    const index = visible.findIndex((step) => step.type === state.stepId);
    const current = visible[index];
    const isLast = index === visible.length - 1;
    state.visited.add(state.stepId);
    root.toggleAttribute('data-started', state.started);
    document.body.toggleAttribute('data-configurator-started', state.started);

    allSteps.forEach((step) => {
      step.el.classList.toggle('is-active', step.type === state.stepId);
      if (step.def.field) syncRadios(step);
    });

    $$('[data-product-group]').forEach((group) => {
      group.style.display = group.dataset.productGroup === state.product ? '' : 'none';
    });

    renderDimmerOptions();

    $$('[data-length-preset]').forEach((button) => {
      button.setAttribute('aria-pressed', String(parseFloat(button.dataset.lengthPreset) === state.length));
    });

    totalEl.textContent = formatMoney(subtotal());
    lengthEl.textContent = summaryText();
    renderSubtotalLabel(visible);
    // Until the length step is reached the total would only reflect a default
    // length, so keep it hidden and let the tape cards show per-metre prices.
    subtotalEl.hidden = !state.visited.has('length');
    renderStepper(visible, index);

    prevBtn.disabled = index === 0;
    nextBtn.style.display = isLast ? 'none' : '';
    nextBtn.disabled = !current.def.isValid();
    addToCartBtn.style.display = isLast ? '' : 'none';
    addToCartBtn.disabled = isAdding || !visible.every((step) => step.def.isValid());
    addToCartBtn.setAttribute('aria-busy', String(isAdding));

    renderPreview(current);
  }

  // The total starts from default choices, so call it an estimate until every
  // step has been reached.
  function renderSubtotalLabel(visible) {
    const allReached = visible.every((step) => state.visited.has(step.type));
    subtotalLabelEl.textContent = allReached ? 'Subtotal:' : 'Estimated subtotal:';
  }

  // Footer summary: grows with each step the customer has reached.
  function summaryText() {
    const product = products.get(state.product);
    const channel = product && product.channels.find((c) => c.id === state.channel);
    const variant = product && product.variants.find((v) => v.id === state.variant);
    const endCap = selectedEndCap();
    const dimmer = state.dimmer !== null && config.dimmers[Number(state.dimmer)];
    const reached = (type) => state.visited.has(type);
    return [
      reached('product') && product && product.title,
      reached('length') && state.length ? `${state.length} m` : null,
      reached('variant') && variant && variant.title,
      reached('channel') && channel && channel.title,
      reached('end-cap') && endCap && endCap.title,
      reached('dimmer') && dimmer && dimmer.title,
    ]
      .filter(Boolean)
      .join(', ');
  }

  // Stepper: one button per step, created once and updated in place so
  // keyboard focus survives a click.
  const stepperItems = new Map();
  const stepperList = document.createElement('ol');
  allSteps.forEach((step) => {
    const li = document.createElement('li');
    const button = document.createElement('button');
    const num = document.createElement('span');
    const label = document.createElement('span');
    button.type = 'button';
    button.dataset.goto = step.type;
    num.className = 'stepper__num';
    label.className = 'stepper__label';
    label.textContent = step.el.dataset.stepLabel || step.type;
    button.append(num, label);
    li.append(button);
    stepperList.append(li);
    stepperItems.set(step.type, { li, button, num });
  });
  progressEl.append(stepperList);

  // A step can be opened once every step before it is complete.
  function renderStepper(visible, currentIndex) {
    allSteps.forEach((step) => {
      const item = stepperItems.get(step.type);
      const index = visible.indexOf(step);
      item.li.hidden = index === -1;
      if (index === -1) return;
      const isCurrent = index === currentIndex;
      item.num.textContent = index + 1;
      item.li.dataset.state = isCurrent ? 'current' : state.visited.has(step.type) ? 'visited' : 'upcoming';
      item.button.disabled = !visible.slice(0, index).every((prior) => prior.def.isValid());
      if (isCurrent) item.button.setAttribute('aria-current', 'step');
      else item.button.removeAttribute('aria-current');
    });
    const label = visible[currentIndex].el.dataset.stepLabel;
    statusEl.textContent = `Step ${currentIndex + 1} of ${visible.length}: ${label}`;
  }

  // Radios of inactive products are cleared so only the visible group is checked.
  function syncRadios(step) {
    step.el.querySelectorAll('input[type="radio"]').forEach((input) => {
      const group = input.closest('[data-product-group]');
      const inActiveGroup = !group || group.dataset.productGroup === state.product;
      input.checked = inActiveGroup && input.value === state[step.def.field];
    });
  }

  // Each dimmer shows its price relative to the first option, and is disabled
  // when no driver kit can power the chosen length.
  function renderDimmerOptions() {
    const options = $$('.dimmer-option');
    if (!options.length) return;
    const baseDriver = pickDriver(config.dimmers[0]);
    const basePrice = baseDriver ? driverTotal(baseDriver) : 0;

    options.forEach((option) => {
      const driver = pickDriver(config.dimmers[Number(option.dataset.dimmerIndex)]);
      option.querySelector('input').disabled = !driver;
      option.style.opacity = driver ? '1' : '0.5';
      const priceEl = option.querySelector('.dimmer-price-display');
      if (!priceEl) return;
      if (driver) {
        const diff = driverTotal(driver) - basePrice;
        priceEl.textContent = diff === 0 ? 'Included' : (diff < 0 ? '-' : '+') + formatMoney(Math.abs(diff));
      } else {
        priceEl.textContent = 'Not available for this length';
      }
    });
  }

  // Only steps with a relevant image change the preview; otherwise it stays put.
  function renderPreview(current) {
    if (!previewEl) return;
    const src = current.def.image ? current.def.image() : null;
    if (src && previewEl.getAttribute('src') !== src) previewEl.setAttribute('src', src);
    previewEl.classList.toggle('hidden', !previewEl.getAttribute('src'));
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(amount, (window.theme && window.theme.moneyFormat) || config.moneyFormat);
      } catch (e) {}
    }
    return (amount / 100).toLocaleString(undefined, { style: 'currency', currency: config.currency });
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  root.addEventListener('change', (event) => {
    const input = event.target;
    const stepEl = input.closest && input.closest('[data-step]');
    const def = stepEl && STEPS[stepEl.dataset.stepType];
    if (!def) return;

    if (def.onChange) def.onChange(input);
    else state[def.field] = input.value;
    render();
  });

  root.addEventListener('click', (event) => {
    const preset = event.target.closest('[data-length-preset]');
    if (!preset) return;
    lengthInput.value = preset.dataset.lengthPreset;
    STEPS.length.onChange(lengthInput);
    render();
  });

  function showStep(type) {
    state.stepId = type;
    render();
    // Only scroll when the new step's heading has been left above the viewport;
    // otherwise leave the page where the customer put it.
    const heading = allSteps.find((step) => step.type === type).el;
    if (heading.getBoundingClientRect().top < 0) heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function goToStep(offset) {
    const visible = visibleSteps();
    const index = visible.findIndex((step) => step.type === state.stepId);
    showStep(visible[Math.max(0, Math.min(index + offset, visible.length - 1))].type);
  }

  progressEl.addEventListener('click', (event) => {
    const button = event.target.closest('[data-goto]');
    if (button && !button.disabled) showStep(button.dataset.goto);
  });

  nextBtn.addEventListener('click', () => goToStep(1));
  prevBtn.addEventListener('click', () => goToStep(-1));

  // Document-wide so CTAs in other sections (e.g. configurator-cta) can start it too.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-start]')) return;
    state.started = true;
    render();
    const heading = $('[data-step].is-active h2');
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  });

  function reset() {
    // Stay in the configurator after a successful add to cart.
    const { started } = state;
    state = createState();
    state.started = started;
    lengthInput.value = defaultLength;
    if (previewEl && initialPreviewSrc) previewEl.setAttribute('src', initialPreviewSrc);
    render();
    progressEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---------------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------------

  function showError(message) {
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  function setAdding(value) {
    isAdding = value;
    render();
  }

  async function addToCart(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (isAdding) return;

    const items = buildLines().map((line) => {
      const item = { id: Number(line.id), quantity: line.quantity };
      if (line.properties) item.properties = line.properties;
      return item;
    });
    if (items.length === 0) return;

    setAdding(true);
    showError('');

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    let url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    if (cart && typeof cart.getSectionsToRender === 'function') {
      try {
        const ids = cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
        const sectionsParam = ids ? `sections=${encodeURIComponent(ids)}` : '';
        const sectionsUrlParam = `sections_url=${encodeURIComponent(window.location.pathname)}`;
        url += `?${[sectionsParam, sectionsUrlParam].filter(Boolean).join('&')}`;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      } catch (e) {}
    }

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (data && data.status) {
        console.error('Add to cart error', data);
        showError(data.description || data.message || 'Unable to add items to cart. Please try again.');
        return;
      }
      if (cart && typeof cart.renderContents === 'function') {
        try {
          cart.renderContents(data);
        } catch (e) {
          if (window.routes && window.routes.cart_url) window.location = window.routes.cart_url;
        }
      } else if (window.routes && window.routes.cart_url) {
        window.location = window.routes.cart_url;
      }
      reset();
    } catch (e) {
      console.error(e);
      showError('Something went wrong adding your items to the cart. Please try again.');
    } finally {
      setAdding(false);
    }
  }

  // Capture phase so we run before the theme's own add-to-cart handlers.
  addToCartBtn.addEventListener('click', addToCart, true);

  render();
}

// A custom element (like Dawn's own components) so the configurator also
// initialises when the theme editor re-renders the section.
if (!customElements.get('product-configurator')) {
  customElements.define(
    'product-configurator',
    class ProductConfigurator extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;
        initConfigurator(this);
      }
    },
  );
}
